<template>
  <section>
    <one-line-form :submit="updateDOB" :submittable="isValid" :saving="isSaving ? isValidDate.dateFormatted : ''">
      <section class="dob-container" :class="{'animate-placeholder': animatePlaceholder}">
        <label class="floating-placeholder" v-if="!isSaving">{{ i18n.t(`birthdate:placeholder`) }}</label>
        <div class="dob-input-wrap" v-for="(input, index) in inputOrder">
          <label>{{ i18n.t(`birthdate:${input}`) }}</label>
          <input type="text"
            class="dob-input"
            autocomplete="off"
            :name="input.toLowerCase()"
            v-model="dateData[input].value"
            :class="{ saving: isSaving,
                      filled: input === 'year' && dateData[input].value.length === maxYearLength || dateData[input].value.length === 2 && input !== 'year',
                      error: dateData[input].error}"
            @input="syncBirthdate(input, $event)"
            @focus="setFocusIndex(index)"
            @keydown.delete="focusPrevious"
            @keydown.left="goToPreviousField"
            @keydown.right="goToNextField"
            @blur="validateInput(input,index)"
            :maxlength="input === 'year' ? maxYearLength : 2"
            v-keyboard="'tel'"
            :ref="input">
            <span class="fake-placeholder">
              {{ fakePlaceholder(input) }}
            </span>
        </div>
      </section>
    </one-line-form>
    <helper v-show="!isSaving && !isValidDate.strict"><p>{{ i18n.t('base:format') }} {{ formatHint }}</p></helper>
    <helper v-show="!isSaving && isValidDate.strict && !isBirthdateValid" class="error"><p>{{ i18n.t('birthdate:age_requirement', {age: minimumBirthAge}) }}</p></helper>
    <helper v-show="!isSaving && isInvalidForm" class="error"><p>{{ i18n.t('birthdate:check_format') }}</p></helper>
    <show-keyboard></show-keyboard>
  </section>
</template>

<script>
  import Vue from 'vue'
  import { mapGetters, mapState } from 'vuex'
  import { getNestedProperty } from '../utils/structure.js'
  import moment from 'moment'

  // eslint-disable-next-line no-useless-escape
  const delimeterRegExp = /[.,\/ -]/

  export default Vue.component('open-question-screen', {
    props: ['screenParams'],
    data () {
      const key = this.screenParams && this.screenParams.dispatchKey ? this.screenParams.dispatchKey.replace('update', '').toLowerCase() : 'user'
      const propertyName = (this.screenParams && this.screenParams.propertyName) || 'birthdate'
      let propertyEntity = propertyName.split('.')
      propertyEntity = propertyEntity.length > 1 ? propertyEntity.slice(0, -1).join('.') : false
      const entityData = propertyEntity ? getNestedProperty(this.$store.state[key], propertyEntity) : this.$store.state[key]

      return {
        dob: entityData.birthdate || '',
        animatePlaceholder: false,
        dateData: {
          day: {
            value: (entityData.birthdate && moment(entityData.birthdate).format('DD')) || '',
          },
          month: {
            value: (entityData.birthdate && moment(entityData.birthdate).format('MM')) || '',
          },
          year: {
            value: (entityData.birthdate && moment(entityData.birthdate).format('YYYY')) || '',
          },
        },
        focusIndex: 0,
        isInvalidForm: false,
        storeKey: key,
        propertyName: propertyName,
        dispatchKey: (this.screenParams && this.screenParams.dispatchKey) || 'updateUser',
        isSaving: false,
      }
    },
    created () {
      if (!this.dob) {
        this.animatePlaceholder = true
        let animatePlaceholderTimer = setTimeout(() => {
          this.animatePlaceholder = false
          clearTimeout(animatePlaceholderTimer)
        }, 1000)
      } else {
        this.animatePlaceholder = false
      }
    },
    mounted () {
      this.$nextTick(() => {
        this.dob ? this.setFocusIndex(2) : this.setFocusIndex(0)
      })
      this.$emit('tooltip', ['type'])

      if (this.isJapaneseFlow) {
        this.handleCompsitionInput()
      }
  },
    watch: {
      'focusIndex' (index) {
        this.$refs[this.inputOrder[index]][0].focus()
      },
      isValid () {
        this.$emit('tooltip', this.isValid ? ['enter'] : ['type'])
      },
      isSaving () {
        if (this.isSaving) {
          this.$emit('tooltip', 'hide')
        }
      },
    },
    computed: {
      ...mapState(['user', 'partner', 'app']),
      ...mapGetters(['isJapaneseFlow']),
      inputDelimeter () {
        const index = this.dobFormat.search(delimeterRegExp)
        return this.dobFormat[index]
      },
      dobFormat () {
        return (this.app.country && this.app.country.dobFormat) || 'DD MM YY'
      },
      inputOrder () {
        if (this.inputDelimeter) {
          return this.dobFormat
            .replace('DD', 'day')
            .replace('MM', 'month')
            .replace('YY', 'year')
            .split(this.inputDelimeter)
        }

        let formattedArray = this.dobFormat.match(/.{2}/g)
        return formattedArray.join(',').replace('DD', 'day')
          .replace('MM', 'month')
          .replace('YY', 'year')
          .split(',')
      },
      maxYearLength () {
        return (['19', '20'].includes(this.dateData.year.value.slice(0, 2)) || this.dateData.year.value.length > 2) ? 4 : 2
      },
      formatHint () {
        const dobFormat = this.dobFormat
        const YY = this.i18n.t('birthdate:yearFormat')
        const MM = this.i18n.t('birthdate:monthFormat')
        const DD = this.i18n.t('birthdate:dayFormat')
        return dobFormat.replace(/YY/, YY).replace(/MM/, MM).replace(/DD/, DD)
      },
      isValidDate () {
        const {day, month, year} = this.dateData
        const pattern = this.maxYearLength === 4 ? this.dobFormat.replace(/YY/, 'YYYY') : this.dobFormat
        const dateStrict = this.dobFormat
          .replace(/YY/, this.padsValue(year.value))
          .replace(/MM/, this.padsValue(month.value))
          .replace(/DD/, this.padsValue(day.value))

        const dateSimple = this.dobFormat
          .replace(/YY/, this.padsValue(year.value) || '92')
          .replace(/MM/, this.padsValue(month.value) || '12')
          .replace(/DD/, this.padsValue(day.value) || '01')

        moment.parseTwoDigitYear = (yearString) => {
          return yearString < 19 ? parseInt(yearString) + 2000 : parseInt(yearString) + 1900
        }

        return {
          strict: moment(dateStrict, pattern, true).isValid(),
          simple: moment(dateSimple, pattern).isValid(),
          date: moment.utc(dateStrict, pattern).toDate(),
          dateFormatted: dateStrict,
        }
      },
      minimumBirthAge () {
        return this.partner.minimumBirthAge[this.user.country] ? this.partner.minimumBirthAge[this.user.country] : this.partner.minimumBirthAge.default
      },
      isBirthdateValid () {
        if (this.isValidDate.strict) {
          return this.getAge(this.isValidDate.date) >= this.minimumBirthAge
        }
        return false
      },
      isValid () {
        return this.isBirthdateValid && this.isValidDate.strict
      },
    },
    methods: {
      validateInput (inputName, index) {
        if (this.dateData[inputName].value === '0') {
          this.dateData[inputName].error = true
          this.isInvalidForm = true
          this.$refs[this.inputOrder[index]][0].focus()
        }
      },
      padsValue (val) {
        return val && val.padStart(2, 0)
      },
      handleCompsitionInput () {
        this.inputOrder.forEach((input, index) => {
          const inputEl = this.$el.querySelector(`input[name=${input}]`)
          if (inputEl) {
            inputEl.addEventListener('compositionend', (e) => {
              const inputValue = this.dateData[input].value
              this.dateData[input].value = inputValue.replaceAll(/[^\d]/gu, '')

              if (isNaN(inputValue)) {
                this.dateData[input].error = false
                this.isInvalidForm = false
                this.$nextTick(() => { this.setFocusIndex(index) })
              }
            })
          }
        })
      },
      updateDOB () {
        this.isSaving = true

        let dispatchData = {}
        dispatchData[this.propertyName] = this.isValidDate.date
        this.$store.dispatch(this.dispatchKey, dispatchData)

        this.trackPrefill('birthdate', this.isValidDate.date)
        return this.$store.dispatch('next', this.screenParams.nextScreenRoute)
      },
      getAge (parsedBirthdate) {
        var today = new Date()
        var age = today.getFullYear() - parsedBirthdate.getFullYear()
        var m = today.getMonth() - parsedBirthdate.getMonth()
        if (m < 0 || (m === 0 && today.getDate() < parsedBirthdate.getDate())) {
          age--
        }
        return age
      },
      syncBirthdate (inputName, e) {
        const value = e.data
        if (value && !value.match(/[0-9]/)) {
          this.dateData[inputName].value = this.dateData[inputName].value.slice(0, -1)
        }

        this.validateDate(inputName)

        this.$nextTick(() => {
          this.focusNext(e)
        })
      },
      validateDate (inputName) {
        const valueLength = inputName === 'year' ? this.maxYearLength : 2

        this.dateData[inputName].error = !this.isValidDate.simple && this.dateData[inputName].value.length === valueLength

        if (!this.isInvalidForm) {
          this.isInvalidForm = !!this.dateData[inputName].error
        }

        if (this.isValidDate.simple) {
          this.isInvalidForm = false
          Object.keys(this.dateData).forEach((propertyName) => {
            if (this.dateData[propertyName].error) {
              this.dateData[propertyName].error = false
            }
          })
        }
      },
      setFocusIndex (index) {
        this.focusIndex = index
      },
      focusNext (e) {
        if (e.target.value.length === e.target.maxLength && e.target.selectionStart === e.target.value.length && this.focusIndex < this.inputOrder.length - 1 && !this.isInvalidForm) {
          this.focusIndex++
        }
      },
      focusPrevious (e) {
        if (e.target.selectionStart === 0 && e.target.selectionEnd === 0 && this.focusIndex > 0) {
          e.preventDefault()
          this.focusIndex--
          const inputName = this.inputOrder[this.focusIndex]

          this.dateData[inputName].value = this.dateData[inputName].value.slice(0, -1)
          this.validateDate(inputName)
        }
      },
      goToPreviousField (e) {
        if (e.target.selectionStart === 0 && this.focusIndex > 0) {
          e.preventDefault()
          this.focusIndex--
          const input = this.$refs[this.inputOrder[this.focusIndex]][0]
          input.setSelectionRange(input.value.length, input.value.length)
        }
      },
      goToNextField (e) {
        if (e.target.selectionStart === e.target.maxLength && this.focusIndex < this.inputOrder.length - 1) {
          this.focusIndex++
          const input = this.$refs[this.inputOrder[this.focusIndex]][0]
          input.setSelectionRange(0, 0)
        }
      },
      fakePlaceholder (inputName) {
        const lang = this.app.moduleLanguage
        const format = ['ja'].includes(lang) ? this.i18n.t(`birthdate:${inputName}`) : this.i18n.t(`birthdate:${inputName}Format`)
        const placeholder = format.length > 1 ? format.slice(0, -1) : format
        const value = this.dateData[inputName].value
  
        if (inputName === 'year') {
          return this.maxYearLength === 4 ? `${value}${placeholder.repeat(4 - value.length)}` : `${value}${placeholder.repeat(2 - value.length)}`
        }
  
        if (inputName === this.inputOrder[this.focusIndex]) {
          return `${value}${placeholder.repeat(2 - value.length)}`
        } else {
          return value && value.toString().length === 1 ? `${value}${placeholder.repeat(1 - value.length)}` : `${value}${placeholder.repeat(2 - value.length)}`
        }
      },
    },
  })
</script>

<style lang="scss" scoped>
  .dob-container {
    display: flex;
    align-items: center;
    height: 50px;
    position: relative;

    &:before {
      content: '';
      background: rgb(233, 234, 235);
      height: 2px;
      position: absolute;
      top: calc(100% - 1px);
      transition: width .4s ease;
      width: 0;
    }

    .dob-input-wrap {
      display: flex;
      align-items: center;

      color: $inputPlaceholderColor;
      color: $inputPlaceholderColor-portal;
      font-weight: $inputPlaceholderWeight;
      font-weight: $inputPlaceholderWeight-portal;
      font-size: $inputPlaceholderSize;
      font-size: $inputPlaceholderSize-portal;
      opacity: 1;
      margin-right: 10px;
      border-bottom: 2px solid rgb(233, 234, 235);
      padding-right: 10px;

      .dob-input {
        position: absolute;
        z-index: 1;
        max-width: 70px;

        &.filled + .fake-placeholder + .delimeter {
          color: $inputActiveColor;
          color: $inputActiveColor-portal;
        }

        &.error {
          color: $inputWarningColor;
          color: $inputWarningColor-portal;
          animation: error-animation .3s forwards ease;

         & + .fake-placeholder {
            animation: fake-placeholder-error-animation .3s forwards ease;
          }
        }

        &[disabled] {
          opacity: 1;
          -webkit-text-fill-color: $inputActiveColor;
          -webkit-text-fill-color: $inputActiveColor-portal;
        }
      }
      .fake-placeholder {
        line-height: 50px;
        min-width: 40px;
        transition: opacity .4s .4s ease;
        font-size: $inputActiveSize !important;
      }
    }
  }

  @keyframes error-animation {
    0% {
      transform: translate3d(0, 0, 0);
    }
    50% {
      transform: translate3d(-5px, 0, 0);
    }
    100% {
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes fake-placeholder-error-animation {
    0% {
      opacity: 0;
    }
    99% {
     opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .animate-placeholder {
    &::before {
      width: 100%;
    }

    .dob-input-wrap {
      & > label,
      .fake-placeholder {
        opacity: 0;
      }
    }
  }

  label {
    font-size: $inputFloatingLabelSize;
    font-size: $inputFloatingLabelSize-portal;
    color: $inputFloatingLabelColor;
    color: $inputFloatingLabelColor-portal;
    font-weight: $inputFloatingLabelWeight;
    font-weight: $inputFloatingLabelWeight-portal;
    position: absolute;
    bottom: 100%;
    transition: opacity .4s .2s ease;
  }

  .floating-placeholder {
    position: absolute;
    transition: all .4s ease;
    bottom: 100%;
    opacity: 0;

    .animate-placeholder & {
      color: $inputPlaceholderColor;
      color: $inputPlaceholderColor-portal;
      font-weight: $inputPlaceholderWeight;
      font-weight: $inputPlaceholderWeight-portal;
      font-size: $inputPlaceholderSize;
      font-size: $inputPlaceholderSize-portal;
      bottom: 15px;
      opacity: 1;
    }
  }

  // IE11 hack
  _:-ms-fullscreen, :root .dob-input {
    top: 0;
  }
</style>



// WEBPACK FOOTER //
// src/screens/PersonalMaskedBirthdate.vue